import {
    WsReactBaseComponentInterface, WsReactBaseContainer, WsReactBaseService,
} from '@thewhite/react-base-components';
import { FlexBox } from '@thewhite/react-flex-layout';
import { GlobalStore } from '@thewhite/react-global-module/lib/store';
import classnames from 'classnames';
import isEqual from 'lodash.isequal';
import { inject, observer } from 'mobx-react';
import * as plural from 'plural-ru';
import * as React from 'react';
import { Redirect } from 'react-router-dom';
import ReactSVG from 'react-svg';
import { toast, ToastContainer, ToastPosition } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import USER_AUTHORITIES from '../../Auth/enums/userAuthorities';
import { AuthService } from '../../Auth/services/authServices';
import { ChatActions } from '../../Chat/actions/chatActions';
import Chat from '../../Chat/model/chat';
import { MainMenu } from '../../Global/components/mainMenu/mainMenu';
import Preloader from '../../Global/components/preloader/preloader';
import BASE_PROGRESS_STATUS from '../../Global/enums/baseProgressStatus';
import { ToolService } from '../../Global/services/toolService';
import { RefStore } from '../../Global/store/refStore';
import { ParticipantsActions } from '../../Participant/actions/participantsActions';

interface IMainWrapProps {
    children: any;
    globalStore?: GlobalStore;
    location: any;
    refStore?: RefStore;
}

export interface IMainWrap extends WsReactBaseComponentInterface {
    props: IMainWrapProps;
    startRedirect: boolean;

    appMainContentRef: React.RefObject<HTMLDivElement>;

    checkUnreadMessagesInterval: number | null;
    checkUnreadMessagesTimeout: number;

    checkParticipantsOnModerationInterval: number | null;
    checkParticipantsOnModerationTimeout: number;

}

@inject('globalStore', 'refStore')
@observer
export class MainWrap extends WsReactBaseContainer<IMainWrapProps, {}> implements IMainWrap {
    appMainContentRef: React.RefObject<HTMLDivElement> = React.createRef();
    startRedirect: boolean = false;
    checkUnreadMessagesInterval: number | null = null;
    checkUnreadMessagesTimeout: number = 30000;

    checkParticipantsOnModerationInterval: number | null = null;
    checkParticipantsOnModerationTimeout: number = 30000;

    constructor(props: IMainWrapProps) {
        super(props);
        if (this.props.globalStore) {
            this.props.globalStore.handlerPressControl();
        }
    }

    componentDidMount() {
        if (this.props.globalStore) {
            this.props.globalStore.setLocation(this.props.location);
        }
        if (this.appMainContentRef && this.appMainContentRef.current) {
            this.props.refStore?.setMainContentRef(this.appMainContentRef);
        }
        this.checkAuth();
        this.checkUnreadMessagesInterval = ToolService.noDelaySetInterval(
            this.checkChatsWithUnreadMessages,
            this.checkUnreadMessagesTimeout,
        );

        if (AuthService.userHasOneRole([
            USER_AUTHORITIES.ADMIN.value,
            USER_AUTHORITIES.BOOKING_OPERATOR.value,
        ])) {
            this.checkParticipantsOnModerationInterval = ToolService.noDelaySetInterval(
                this.checkParticipantsOnModeration,
                this.checkParticipantsOnModerationTimeout,
            );
        }
    }

    componentWillReceiveProps(newProps: IMainWrapProps) {
        if (!isEqual(newProps.location, this.props.location) && !!this.props.globalStore) {
            this.props.globalStore.setLocation(newProps.location);
        }
    }

    componentWillUnmount = () => {
        if (this.checkUnreadMessagesInterval) {
            window.clearInterval(this.checkUnreadMessagesInterval);
        }

        if (this.checkParticipantsOnModerationInterval) {
            window.clearInterval(this.checkParticipantsOnModerationInterval);
        }
    }

    /**
     * Проверка авторизации
     */
    checkAuth = (): void => {
        const userInfo = AuthService.getUserInfo();
        if (!userInfo) AuthService.logOut();
    }

    /**
     * Проверить, есть ли чаты с непрочитанными сообщениями
     */
    checkChatsWithUnreadMessages = (): void => {
        ChatActions.list({ hasUnreadMessages: true })
            .then((chats: Chat[]) => {
                const chatsWithUnreadMessages: number = chats.length;
                if (!chatsWithUnreadMessages) return;

                const chatsCountStringForm: string =
                          plural.noun(chatsWithUnreadMessages, '%d чат', '%d чата', '%d чатов');
                const message: string = `У вас ${chatsCountStringForm} с непрочитанными сообщениями`;
                WsReactBaseService.showToast(toast.info(message));
            });
    };

    /**
     * Проверить, есть ли необработанные заявки
     */
    checkParticipantsOnModeration = (): void => {
        ParticipantsActions.getCountByStatus(BASE_PROGRESS_STATUS.MODERATION.value)
            .then((count: number) => {
                if (!count) return;

                const countStringForm: string = plural.noun(
                    count,
                    '%d необработанная заявка',
                    '%d необработанные заявки',
                    '%d необработанных заявок',
                );
                const message: string = `У вас ${countStringForm}`;
                WsReactBaseService.showToast(toast.info(message));
            });
    };

    /**
     * Получить строку со значением текущего года
     */
    getCurrentYear = (): string => {
        return this.moment()
            .year()
            .toString();
    }

    render(): false | JSX.Element {
        if (!!this.props.globalStore && !!this.props.globalStore.redirect && !this.startRedirect) {
            this.startRedirect = true;
        } else if (this.startRedirect && !!this.props.globalStore) {
            this.startRedirect = false;
            this.props.globalStore.clearRedirect();
        }

        return (
            <>
                {
                    !!this.props.globalStore && !!this.props.globalStore.redirect && this.startRedirect &&
                        <Redirect
                            to={this.props.globalStore.redirect}
                            push={true}
                        />
                }
                <FlexBox
                    className={classnames(
                        'main-wrap',
                    )}
                    row={'start stretch'}
                >
                    <div
                        className={'main-wrap__content-box'}
                    >
                        <main
                            className={classnames(
                                'main-wrap__main-content',
                                'app-main',
                            )}
                            ref={this.appMainContentRef}
                        >
                            {
                                this.props.globalStore && this.props.globalStore.loading &&
                                    <Preloader/>
                            }
                            {
                                !!this.props.globalStore && !!this.props.globalStore.redirect && this.startRedirect
                                    ?
                                        <Redirect
                                            to={this.props.globalStore.redirect}
                                            push={true}
                                        />
                                    :
                                        this.props.children
                            }
                        </main>
                        <footer
                            className={classnames(
                                'main-wrap__content-footer',
                                'app-footer',
                            )}
                        >
                            <FlexBox
                                <HTMLAnchorElement>
                                node={'a'}
                                row={'ctr'}
                                className={'app-footer__footer-created-by-link'}
                                href={'https://thewhite.ru/'}
                                target={'_blank'}
                                rel={'noopener noreferrer'}
                            >
                                <ReactSVG
                                    className={'app-footer__footer-created-by-link-icon-wrapper'}
                                    src={'assets/images/svg/white.svg'}
                                    svgClassName={'app-footer__footer-created-by-link-icon'}
                                />
                            </FlexBox>
                        </footer>
                    </div>
                    <FlexBox
                        shrink={'0'}
                        node={'aside'}
                        column={'start stretch'}
                        className={classnames(
                            'main-wrap__sidebar',
                            'app-sidebar',
                        )}
                    >
                        <a
                            href={'#/'}
                            className={classnames(
                                'main-wrap__sidebar-home-link',
                            )}
                        >
                            <ReactSVG
                                className={classnames(
                                    'main-wrap__sidebar-home-link-icon-wrapper',
                                )}
                                src={'assets/images/svg/logo.svg'}
                                svgClassName={'main-wrap__sidebar-home-link-icon'}
                            />
                        </a>
                        <MainMenu
                            pathName={this.props.location.pathname}
                        />
                    </FlexBox>
                    <ToastContainer
                        toastClassName="global-toast"
                        position={ToastPosition.BOTTOM_RIGHT}
                    />
                </FlexBox>
            </>
        );
    }
}

export default MainWrap;
